import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { auth, googleProvider, facebookProvider, getFCMToken, setupRecaptcha } from '../../lib/firebase';
import { useUserSignupMutation } from '../../features/api/apiSlice';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import SocialLogin from './components/SocialLogin';
import SecurityFeatures from './components/SecurityFeatures';
import RegistrationPrompt from './components/RegistrationPrompt';
import Icon from '../../components/AppIcon';
import { useToast } from '../../components/ui/Toast'; // Import useToast

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  // const [error, setError] = useState(''); // Removed local error state
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [authMethod, setAuthMethod] = useState('social'); // 'social' or 'phone'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userSignup] = useUserSignupMutation();
  const dispatch = useDispatch();
  const addToast = useToast(); // Initialize useToast

  // Get redirect path from location state or default to dashboard
  const redirectPath = location.state?.from?.pathname || '/user-dashboard';

  useEffect(() => {
    // Show captcha after 2 failed attempts
    if (loginAttempts >= 2) {
      setShowCaptcha(true);
    }
  }, [loginAttempts]);

  useEffect(() => {
    // Initialize reCAPTCHA verifier once when the component mounts
    // This ensures the reCAPTCHA element is present and initialized correctly
    setupRecaptcha('recaptcha-container');
  }, []);

  // Function to get user location
  const getUserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          addToast('Failed to get your location.', 'error'); // Use toast for error
          resolve({ latitude: null, longitude: null });
        },
        { timeout: 10000 }
      );
    });
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    // setError(''); // Removed local error state

    try {
      // Choose provider
      const authProvider = provider === 'google' ? googleProvider : facebookProvider;

      // Sign in with Firebase
      const result = await signInWithPopup(auth, authProvider);
      const user = result.user;

      // Get additional data
      const location = await getUserLocation();
      const fcmToken = await getFCMToken();

      // Prepare data for API
      const userData = {
        type: provider,
        firebase_id: user.uid,
        name: user.displayName || user.email.split('@')[0],
        email: user.email,
        mobile: user.phoneNumber || '',
        fcm_id: fcmToken || '',
        latitude: location.latitude,
        longitude: location.longitude
      };

      // Call the API
      const response = await userSignup(userData).unwrap();

      // Dispatch credentials to Redux store
      dispatch(setCredentials({
        user: {
          id: response.id || user.uid,
          name: user.displayName || userData.name,
          email: user.email,
          avatar: user.photoURL || '',
          joinDate: new Date().toISOString().split('T')[0],
          preferences: {
            notifications: true,
            newsletter: true
          }
        },
        token: response.token,
        customerId: response.id || user.uid,
      }));

      addToast('Login successful!', 'success'); // Show success toast
      // Navigate to intended destination
      // Use window.location.href for a full page reload to ensure state is re-initialized
      // This solves issues where the app doesn't "refresh" after login.
      window.location.href = redirectPath;

    } catch (err) {
      console.error('Social login error:', err);
      addToast(`${provider} login failed. Please try again.`, 'error'); // Use toast for error
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaVerify = (verified) => {
    setCaptchaVerified(verified);
  };

  const handlePhoneAuth = async () => {
    if (!phoneNumber.trim()) {
      addToast('Please enter a phone number', 'error'); // Use toast for error
      return;
    }

    setIsLoading(true);
    // setError(''); // Removed local error state

    try {
      // Use the already setup reCAPTCHA verifier
      const recaptchaVerifier = window.recaptchaVerifier;
      if (!recaptchaVerifier) {
        addToast('reCAPTCHA not initialized. Please refresh the page.', 'error');
        setIsLoading(false);
        return;
      }

      // Send verification code
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      setConfirmationResult(confirmation);
      setShowVerification(true);
      addToast('Verification code sent to your phone.', 'info'); // Show info toast
    } catch (err) {
      console.error('Phone auth error:', err);
      addToast('Failed to send verification code. Please try again.', 'error'); // Use toast for error
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      addToast('Please enter the verification code', 'error'); // Use toast for error
      return;
    }

    setIsLoading(true);
    // setError(''); // Removed local error state

    try {
      // Verify the code
      const result = await confirmationResult.confirm(verificationCode);
      const user = result.user;

      // Get additional data
      const location = await getUserLocation();
      const fcmToken = await getFCMToken();

      // Prepare data for API
      const userData = {
        type: 'phone',
        firebase_id: user.uid,
        name: user.displayName || user.phoneNumber || 'Phone User',
        email: user.email || '',
        mobile: user.phoneNumber,
        fcm_id: fcmToken || '',
        latitude: location.latitude,
        longitude: location.longitude
      };

      // Call the API
      const response = await userSignup(userData).unwrap();

      // Dispatch credentials to Redux store
      dispatch(setCredentials({
        user: {
          id: response.id || user.uid,
          name: user.displayName || userData.name,
          email: user.email || '',
          avatar: user.photoURL || '',
          joinDate: new Date().toISOString().split('T')[0],
          preferences: {
            notifications: true,
            newsletter: true
          }
        },
        token: response.token,
        customerId: response.id || user.uid,
      }));

      addToast('Login successful!', 'success'); // Show success toast
      // Navigate to intended destination
      // Use window.location.href for a full page reload
      window.location.href = redirectPath;

    } catch (err) {
      console.error('Code verification error:', err);
      addToast('Invalid verification code. Please try again.', 'error'); // Use toast for error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            {/* Header */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                  <Icon name="Home" size={32} color="white" />
                </div>
              </div>
              
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Welcome Back
              </h1>
              <p className="text-muted-foreground">
                Sign in to your HireBroker account to continue your property search
              </p>
            </div>

            {/* Login Form Card */}
            <div className="bg-card border border-border rounded-lg shadow-lg p-6 sm:p-8">
              {/* Auth Method Tabs */}
              <div className="flex border-b border-border mb-6">
                <button
                  onClick={() => setAuthMethod('social')}
                  className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
                    authMethod === 'social'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Social Login
                </button>
                <button
                  onClick={() => setAuthMethod('phone')}
                  className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
                    authMethod === 'phone'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Phone Login
                </button>
              </div>

              {/* General Error Message (Removed) */}
              {/* {error && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-md mb-6">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-error" />
                    <p className="text-sm text-error">{error}</p>
                  </div>
                </div>
              )} */}

              {authMethod === 'social' ? (
                <>
                  {/* Social Login */}
                  <SocialLogin
                    onSocialLogin={handleSocialLogin}
                    isLoading={isLoading}
                  />

                  {/* Security Features */}
                  <div className="mt-6">
                    <SecurityFeatures
                      showCaptcha={showCaptcha}
                      onCaptchaVerify={handleCaptchaVerify}
                    />
                  </div>
                </>
              ) : (
                /* Phone Login */
                <div className="space-y-4">
                  {!showVerification ? (
                    <>
                      <Input
                        label="Phone Number"
                        type="tel"
                        placeholder="+1234567890"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                      <Button
                        type="button"
                        variant="default"
                        size="lg"
                        fullWidth
                        onClick={handlePhoneAuth}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        Send Verification Code
                      </Button>
                    </>
                  ) : (
                    <>
                      <Input
                        label="Verification Code"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                      <Button
                        type="button"
                        variant="default"
                        size="lg"
                        fullWidth
                        onClick={handleVerifyCode}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        Verify Code
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        fullWidth
                        onClick={() => {
                          setShowVerification(false);
                          setVerificationCode('');
                          setConfirmationResult(null);
                        }}
                        disabled={isLoading}
                      >
                        Back to Phone Number
                      </Button>
                    </>
                  )}
                  {/* reCAPTCHA container */}
                  {/* reCAPTCHA container */}
                  {/* This div needs to be present in the DOM for reCAPTCHA to initialize */}
                  {/* It will be managed by Firebase reCAPTCHA itself */}
                  {/* The reCAPTCHA container is now outside the conditional rendering to ensure it's always in the DOM. */}
                </div>
              )}
              <div id="recaptcha-container" style={{ position: 'absolute', left: '-9999px' }}></div>
            </div>

            {/* Registration Prompt */}
            <RegistrationPrompt isLoading={isLoading} />

            {/* Privacy Notice */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                By signing in, you agree to our{' '}
                <button className="text-primary hover:underline">
                  Terms of Service
                </button>{' '}
                and{' '}
                <button className="text-primary hover:underline">
                  Privacy Policy
                </button>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
